import type { ReactNode } from 'react'
import { Outlet, createRootRoute, HeadContent, Scripts, useParams } from '@tanstack/react-router'
import { isLocale } from '@/i18n/config'
import { UMAMI_WEBSITE_ID } from '@/lib/analytics/umami'
import { NavigationProgress } from '@/components/navigation-progress'
import { NotFoundPage } from '@/components/not-found'
import { RootErrorPage } from '@/components/root-error'
import appCss from '../styles.css?url'

export const Route = createRootRoute({
  // TanStack Start sets the HTTP 404 status implicitly whenever this (or
  // router.tsx's defaultNotFoundComponent) renders — no explicit
  // setResponseStatus call. Load-bearing for SEO: don't remove without
  // adding one back.
  notFoundComponent: () => <NotFoundPage />,
  errorComponent: ({ error, reset }) => <RootErrorPage error={error as Error} reset={reset} />,
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: 'CountAct' },
    ],
    links: [
      { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
      { rel: 'icon', href: '/favicon.ico', type: 'image/x-icon', sizes: '16x16 32x32 48x48' },
      { rel: 'apple-touch-icon', href: '/apple-touch-icon.png', sizes: '180x180' },
      { rel: 'preload', href: '/fonts/TASAOrbiterText-Regular.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
      { rel: 'preload', href: '/fonts/TASAOrbiterDisplay-SemiBold.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
      { rel: 'stylesheet', href: appCss },
    ],
  }),
  component: RootComponent,
})

function RootComponent() {
  return (
    <RootDocument>
      <Outlet />
    </RootDocument>
  )
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>) {
  const { locale } = useParams({ strict: false }) as { locale?: string }
  // Clamp: an unmatched first path segment (e.g. `/de/x`) must not become
  // the document language — the 404 that renders there is always French.
  return (
    <html lang={locale && isLocale(locale) ? locale : 'fr'}>
      <head>
        <HeadContent />
        {/* Umami (self-hosted at analytics.countact.fr), cookieless, replaces
            Plausible. `defer` lets the tracker auto-capture the initial pageview
            and every SPA navigation (it patches window.history). `data-domains`
            gates all sends to the production hostnames, so `pnpm dev` and preview
            deploys never pollute production analytics. Custom events go through
            window.umami.track(...) from src/lib/analytics/track.ts. */}
        <script
          defer
          src="https://analytics.countact.fr/script.js"
          data-website-id={UMAMI_WEBSITE_ID}
          data-domains="countact.fr"
        />
        {/* Session replay + heatmaps recorder (same Umami site). Sampling,
            masking and max-duration are configured server-side in the Umami
            dashboard, not here. */}
        <script
          defer
          src="https://analytics.countact.fr/recorder.js"
          data-website-id={UMAMI_WEBSITE_ID}
          data-domains="countact.fr"
        />
        {/* framer-motion serializes the `initial` state server-side: without
            JS the animated blocks land at opacity:0 and would stay there.
            `!important` is required — we are overriding an inline style. */}
        <noscript>
          <style>{`[data-reveal]{opacity:1!important;transform:none!important}`}</style>
        </noscript>
      </head>
      <body>
        <NavigationProgress />
        {children}
        <Scripts />
      </body>
    </html>
  )
}
